/**
 * LES CHIFFRES DU BILAN BTP — hors du rendu, et testés (btpSummary.test.js).
 *
 * Un pourcentage d'économie projeté en fin d'atelier est LE chiffre que
 * l'animateur commente ; il a été faux pendant des mois parce que la formule
 * vivait dans un `map` de composant, dupliquée dans trois graphes.
 *
 * Deux conventions, tenues partout :
 * - La base est TOUJOURS le projet standard. « −38 % par rapport au projet
 *   standard » se lit (obtenu − standard) / standard. Diviser par l'obtenu
 *   (l'ancienne formule) gonfle l'économie d'autant plus qu'elle est grande :
 *   38 % réels s'affichaient 62 %.
 * - Le pourcentage est ARRONDI ICI, une fois : l'icône est décidée sur le
 *   nombre affiché, jamais sur un décimal que personne ne voit. Un « −20 % »
 *   à l'écran avec une icône « partiel » a été signalé comme un bug — c'en
 *   était un.
 */

/** Seuils d'atteinte de l'objectif, en % d'amélioration par rapport au standard. */
export const REACHED_AT = 20;
export const PARTIAL_AT = 10;

export const RATING_SCORE = { reached: 1, partial: 0.5, missed: 0 };

/**
 * Variation de `value` par rapport à `standard`, en % entier.
 * Négatif = moins que le standard. `null` si le standard ne permet aucun ratio.
 */
export function variationPercent(value, standard) {
  if (!Number.isFinite(value) || !Number.isFinite(standard) || standard === 0) return null;
  return Math.round(((value - standard) / standard) * 100);
}

/**
 * Une variation telle qu'elle s'affiche : signe explicite, « — » sans ratio.
 * La table et la carte CBS passent par ici : elles montrent le même chiffre
 * côte à côte, et la carte en affichait l'opposé (« −77 % d'impact » face à
 * « +77 % »).
 */
export function formatVariation(variation) {
  if (variation === null || variation === undefined) return '—';
  return `${variation > 0 ? '+' : ''}${variation}%`;
}

/**
 * Amélioration en % entier, signée dans le sens du progrès : une empreinte
 * eau (`better: 'lower'`) s'améliore en baissant, un CBS (`better: 'higher'`)
 * en montant. Une dégradation est négative — elle ne peut pas passer pour un
 * objectif atteint, quel que soit son ampleur.
 */
export function improvementPercent(value, standard, better = 'lower') {
  const variation = variationPercent(value, standard);
  if (variation === null) return null;
  return better === 'higher' ? variation : -variation;
}

/** 'reached' | 'partial' | 'missed', décidé sur le pourcentage affiché. */
export function rating(improvement) {
  if (improvement === null || improvement === undefined) return 'missed';
  if (improvement >= REACHED_AT) return 'reached';
  if (improvement >= PARTIAL_AT) return 'partial';
  return 'missed';
}

/**
 * Une ligne de la table « Vos résultats » : les valeurs, la variation telle
 * qu'affichée, et l'évaluation qui en découle.
 */
export function objectiveRow(scoreEntry, better = 'lower') {
  const value = scoreEntry?.value ?? 0;
  const standard = scoreEntry?.standard_value ?? 0;
  const variation = variationPercent(value, standard);
  const improvement = improvementPercent(value, standard, better);
  return { value, standard, variation, improvement, rating: rating(improvement) };
}

/** Part de l'objectif atteinte sur un jeu de lignes, en % entier. */
export function objectivePercent(rows) {
  if (!rows.length) return 0;
  const total = rows.reduce((sum, row) => sum + RATING_SCORE[row.rating], 0);
  return Math.round((total / rows.length) * 100);
}
